import type { ItineraryDay } from '../../../../mocks/itinerary';
import { serviceTypeConfig } from '../../../../mocks/itinerary';
import { agencyInfo } from '../../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

interface Props {
  days: ItineraryDay[];
  pax: number;
  currency: string;
  title: string;
  nights: number;
  destination: string;
  onExportPDF: () => void;
}

export default function PricingSummary({ days, pax, currency, title, nights, destination, onExportPDF }: Props) {
  const allServices = days.flatMap((d) => d.services);
  const totalCost = allServices.reduce((sum, s) => sum + s.costPrice, 0);
  const totalSell = allServices.reduce((sum, s) => sum + s.sellPrice, 0);
  const profit = totalSell - totalCost;
  const margin = totalSell > 0 ? Math.round(((profit) / totalSell) * 100) : 0;
  const perPerson = pax > 0 ? Math.round(totalSell / pax) : totalSell;

  // Group by service type
  const typeBreakdown: Record<string, { cost: number; sell: number; count: number }> = {};
  allServices.forEach((s) => {
    if (!typeBreakdown[s.type]) typeBreakdown[s.type] = { cost: 0, sell: 0, count: 0 };
    typeBreakdown[s.type].cost += s.costPrice;
    typeBreakdown[s.type].sell += s.sellPrice;
    typeBreakdown[s.type].count++;
  });

  const totalServicesCount = allServices.length;

  return (
    <div className="w-72 flex-shrink-0 flex flex-col gap-4">
      {/* Trip Summary */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-50" style={{ backgroundColor: `${brand}08` }}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Trip Summary</p>
        </div>
        <div className="p-4 space-y-2.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500 flex items-center gap-1.5"><i className="ri-map-pin-line" />Destination</span>
            <span className="font-medium text-gray-700 text-right">{destination || '—'}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500 flex items-center gap-1.5"><i className="ri-moon-line" />Nights</span>
            <span className="font-medium text-gray-700">{nights}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500 flex items-center gap-1.5"><i className="ri-calendar-line" />Days Built</span>
            <span className="font-medium text-gray-700">{days.length}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500 flex items-center gap-1.5"><i className="ri-user-line" />Pax</span>
            <span className="font-medium text-gray-700">{pax} pax</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500 flex items-center gap-1.5"><i className="ri-service-line" />Services</span>
            <span className="font-medium text-gray-700">{totalServicesCount}</span>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-50" style={{ backgroundColor: `${brand}08` }}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Pricing ({currency})</p>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Total Cost</span>
            <span className="font-medium text-gray-700">{currency} {totalCost.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Total Sell</span>
            <span className="font-semibold text-gray-800">{currency} {totalSell.toLocaleString()}</span>
          </div>

          <div className="border-t border-gray-100 pt-3">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-500">Gross Profit</span>
              <span className={`font-semibold ${profit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {currency} {profit.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-sm mb-3">
              <span className="text-gray-500">Margin</span>
              <span className={`font-semibold ${margin >= 20 ? 'text-emerald-600' : margin >= 10 ? 'text-amber-500' : 'text-red-500'}`}>
                {margin}%
              </span>
            </div>
            {/* Margin bar */}
            <div className="w-full bg-gray-100 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: `${Math.min(margin, 100)}%`,
                  backgroundColor: margin >= 20 ? '#10B981' : margin >= 10 ? '#F59E0B' : '#EF4444',
                }}
              />
            </div>
          </div>

          <div
            className="rounded-xl p-3 text-center mt-1"
            style={{ backgroundColor: `${brand}10` }}
          >
            <p className="text-xs text-gray-500">Price Per Person</p>
            <p className="text-lg font-bold" style={{ color: brand }}>{currency} {perPerson.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Type Breakdown */}
      {Object.keys(typeBreakdown).length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50" style={{ backgroundColor: `${brand}08` }}>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Breakdown by Type</p>
          </div>
          <div className="p-4 space-y-2.5">
            {Object.entries(typeBreakdown).map(([type, data]) => {
              const cfg = serviceTypeConfig[type as keyof typeof serviceTypeConfig];
              return (
                <div key={type} className="flex items-center gap-2">
                  <span
                    className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: cfg.bg, color: cfg.color }}
                  >
                    <i className={`${cfg.icon} text-xs`} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">{type} ({data.count})</span>
                      <span className="text-xs font-semibold text-gray-700">
                        {data.sell > 0 ? `${currency} ${data.sell.toLocaleString()}` : '—'}
                      </span>
                    </div>
                    {data.sell > 0 && (
                      <div className="w-full bg-gray-100 rounded-full h-1 mt-1">
                        <div
                          className="h-1 rounded-full"
                          style={{
                            width: `${totalSell > 0 ? Math.round((data.sell / totalSell) * 100) : 0}%`,
                            backgroundColor: cfg.color,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PDF Export */}
      <button
        onClick={onExportPDF}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold text-sm cursor-pointer whitespace-nowrap"
        style={{ backgroundColor: brand }}
      >
        <i className="ri-file-pdf-line text-base" />
        Preview & Export PDF
      </button>
    </div>
  );
}
